import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './CustomNotification.module.css';

export default function CustomNotification({ title, description, duration = 4000, onDismiss }) {
    const [visible, setVisible] = useState(false);
    const [fillWidth, setFillWidth] = useState('100%');
    const dismissTimer = useRef(null);
    const animationFrame = useRef(null);
    const startTime = useRef(null);

    const dismiss = () => {
        setVisible(false);
        clearTimeout(dismissTimer.current);
        cancelAnimationFrame(animationFrame.current);
        // Wait for fade-out transition to finish before unmounting
        setTimeout(() => onDismiss?.(), 250);
    };

    useEffect(() => {
        // Trigger entrance animation on next frame
        const enterFrame = requestAnimationFrame(() => setVisible(true));

        // Animate the progress bar draining over `duration`
        startTime.current = performance.now();
        const tick = (now) => {
            const elapsed = now - startTime.current;
            const remaining = Math.max(0, 1 - elapsed / duration);
            setFillWidth(`${remaining * 100}%`);
            if (remaining > 0) {
                animationFrame.current = requestAnimationFrame(tick);
            }
        };
        animationFrame.current = requestAnimationFrame(tick);

        dismissTimer.current = setTimeout(dismiss, duration);

        return () => {
            cancelAnimationFrame(enterFrame);
            cancelAnimationFrame(animationFrame.current);
            clearTimeout(dismissTimer.current);
        };
    }, []);

    const notification = (
        <div className={`${styles.notification} ${visible ? styles.notificationVisible : ''}`}>
            <div className={styles.notificationTop}>
                <div className={styles.notificationIcon}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-5.5" stroke="#60c87a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                <div className={styles.notificationBody}>
                    <span className={styles.notificationTitle}>{title}</span>
                    {description && (
                        <span className={styles.notificationDesc}>{description}</span>
                    )}
                </div>
                <button className={styles.notificationClose} onClick={dismiss} aria-label="Dismiss">
                    ×
                </button>
            </div>
            <div className={styles.notificationProgress}>
                <div className={styles.notificationProgressFill} style={{ width: fillWidth }} />
            </div>
            <div className={styles.notificationFooter}>
                <span className={styles.notificationAutoDismiss}>
                    Auto-dismisses in {Math.round(duration / 1000)}s
                </span>
            </div>
        </div>
    );

    return ReactDOM.createPortal(notification, document.body);
}